import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import { useProject } from '../../context/ProjectContext';
import api from '../../services/api';
import auditService from '../../services/auditService';
import { exportCRFDataReportToExcel } from '../../utils/exportToExcel';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import './ReportsHome.css';
import './AuditReport.css';

/** Visit type options for multi-select; value is stored in DB (VisitType). */
const VISIT_TYPE_OPTIONS = [
  { value: 'SSV', label: 'Site Selection Visit' },
  { value: 'SIV', label: 'Site Initiation Visit' },
  { value: 'SMV', label: 'Site Monitoring Visit' },
  { value: 'SCV', label: 'Site Close Out Visit' },
];

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

function MultiSelectDropdown({ placeholder, options, selectedValues, onChange, disabled, renderLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selectAllRef = useRef(null);
  useOnClickOutside(ref, () => setOpen(false));
  const norm = (v) => (v == null ? '' : String(v));
  const selectedSet = new Set((selectedValues || []).map(norm));
  const allValues = options.map((o) => norm(o.value));
  const allSelected = allValues.length > 0 && allValues.every((v) => selectedSet.has(v));
  const someSelected = selectedSet.size > 0;

  useEffect(() => {
    const el = selectAllRef.current;
    if (el) el.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected, open]);

  const label = renderLabel
    ? options.filter((o) => selectedSet.has(norm(o.value))).map((o) => renderLabel(o)).join(', ')
    : options.filter((o) => selectedSet.has(norm(o.value))).map((o) => o.label).join(', ');
  const summary = selectedValues.length === 0 ? placeholder : (label || `${selectedValues.length} selected`);

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange([...allValues]);
    }
  };

  return (
    <div className="audit-multiselect" ref={ref}>
      <button
        type="button"
        className="form-select audit-report-select audit-multiselect-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        title={summary}
      >
        <span className="audit-multiselect-summary">{summary}</span>
        <span className="audit-multiselect-arrow">▼</span>
      </button>
      {open && (
        <div className="audit-multiselect-dropdown">
          <label className="audit-multiselect-option audit-multiselect-select-all">
            <input
              type="checkbox"
              ref={selectAllRef}
              checked={allSelected}
              onChange={handleSelectAll}
            />
            <span>Select All</span>
          </label>
          {options.map((opt) => (
            <label key={opt.value} className="audit-multiselect-option">
              <input
                type="checkbox"
                checked={selectedSet.has(norm(opt.value))}
                onChange={(e) => {
                  const newSet = new Set((selectedValues || []).map(norm));
                  if (e.target.checked) newSet.add(norm(opt.value));
                  else newSet.delete(norm(opt.value));
                  onChange(Array.from(newSet));
                }}
              />
              <span>{renderLabel ? renderLabel(opt) : opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const CRFDataReport = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { vRecordedSign, vRecordedAtOffset } = useSession();
  const { currentProject: contextProject, projectInfo } = useProject();
  const sessionProject = contextProject && contextProject !== 'all' ? contextProject : null;
  const projectCode = typeof sessionProject === 'string' ? sessionProject : (sessionProject?.projectCode ?? sessionProject?.ProjectCode ?? '');
  const projectLabel = typeof sessionProject === 'object' && sessionProject != null
    ? (sessionProject.projectCode || sessionProject.ProjectCode || sessionProject.projectDescription || '')
    : projectCode;

  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [selectedVisitTypes, setSelectedVisitTypes] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingSites(true);
    const project = projectCode || undefined;
    api.get(project ? `/sites?project=${encodeURIComponent(project)}` : '/sites')
      .then((res) => {
        if (!cancelled) setSites(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setSites([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSites(false);
      });
    return () => { cancelled = true; };
  }, [projectCode]);

  const handleExportExcel = async () => {
    const siteIdList = selectedSiteIds.filter((id) => id != null && id !== '');
    if (siteIdList.length === 0) {
      setError('Please select at least one Site.');
      return;
    }
    setError(null);
    setExportLoading(true);
    try {
      const params = {
        siteIds: siteIdList.map((id) => Number(id)),
        limit: 10000,
      };
      if (projectCode) {
        const pid = projectInfo?.projectId ?? projectInfo?.ProjectId;
        if (pid != null && pid !== '') params.projectId = Number(pid);
        const projNo = projectInfo?.projectNo ?? projectInfo?.ProjectNo
          ?? sessionProject?.projectNo ?? sessionProject?.ProjectNo;
        if (projNo != null) params.projectNo = projNo;
      }
      if (selectedVisitTypes.length > 0) params.visitTypes = selectedVisitTypes;

      const list = await auditService.getCRFDataReport(params);
      const data = Array.isArray(list) ? list : [];
      const fileName = `VisitReport_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const firstName = (user?.firstName ?? user?.FirstName ?? user?.vFirstName ?? '').trim();
      const lastName = (user?.lastName ?? user?.LastName ?? user?.vLastName ?? '').trim();
      const userName = (user?.username ?? user?.Username ?? user?.vLoginName ?? user?.vUserName ?? '').trim();
      const profile = (user?.role ?? user?.Role ?? user?.profileCode ?? user?.vUserTypeCode ?? '').trim();
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      const exportedBy = fullName ? `${fullName} (${userName || '—'})(${profile || '—'})` : (vRecordedSign && vRecordedSign.trim() !== '' ? vRecordedSign.trim() : '');
      const offset = (vRecordedAtOffset && String(vRecordedAtOffset).trim() !== '') ? String(vRecordedAtOffset).trim() : '+5:30';
      exportCRFDataReportToExcel(data, fileName, {
        formName: 'Visit Report',
        exportedBy,
        offset,
      });
    } catch (err) {
      const apiError = err?.response?.data?.error || err?.message || 'Export failed.';
      const message = typeof apiError === 'string' && apiError.toLowerCase().includes('at least one site')
        ? 'Please select at least one Site.'
        : apiError;
      setError(message);
    } finally {
      setExportLoading(false);
    }
  };

  const siteOptions = sites.map((s) => ({
    value: String(s.siteID ?? s.siteId ?? s.SiteID),
    label: `${s.siteCode ?? s.vSiteCode ?? ''} - ${s.sitedescription ?? s.siteDescription ?? ''}`.trim() || String(s.siteID ?? s.siteId),
  }));

  if (loadingSites && sites.length === 0) return <Spinner />;

  return (
    <div className="audit-report">
      <div className="audit-report-header" style={{ marginBottom: '16px' }}>
        <h2>Visit Report</h2>
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="audit-report-filters">
        <div className="audit-report-filter-row">
          <label className="audit-report-label">Project</label>
          <input
            type="text"
            className="form-input audit-report-input"
            value={projectLabel || projectCode || '-'}
            disabled
            readOnly
          />
        </div>
        <div className="audit-report-filter-row">
          <label className="audit-report-label">Site <span className="required">*</span></label>
          <MultiSelectDropdown
            placeholder="Select Site(s)..."
            options={siteOptions}
            selectedValues={selectedSiteIds}
            onChange={setSelectedSiteIds}
            renderLabel={(o) => o.label}
          />
        </div>
        <div className="audit-report-filter-row">
          <label className="audit-report-label">Visit</label>
          <MultiSelectDropdown
            placeholder="All visit types"
            options={VISIT_TYPE_OPTIONS}
            selectedValues={selectedVisitTypes}
            onChange={setSelectedVisitTypes}
            renderLabel={(o) => o.label}
          />
        </div>
      </div>

      <div className="audit-report-actions">
        <button
          type="button"
          className="btn btn-primary export-excel-btn"
          onClick={handleExportExcel}
          disabled={exportLoading || selectedSiteIds.length === 0}
          title={selectedSiteIds.length === 0 ? 'Select at least one site to export' : exportLoading ? 'Exporting…' : 'Export to Excel'}
        >
          {exportLoading ? 'Exporting…' : 'Export'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/reports')}>
          Close
        </button>
      </div>
    </div>
  );
};

export default CRFDataReport;
